import { isTieredVariant } from '@lub0v/santa-utils';
import { PremiumOfferView } from '@src/components/PremiumUpsell/PremiumOfferView';
import { premiumFeatureAction } from '@src/pages/PremiumPage/featureTier';
import { usePremiumOffer } from '@src/pages/PremiumPage/PremiumOfferProvider';
import { THEME_ORDER_EXPERIMENT, type ThemeOrderAssignment, type ThemeClickPlacement } from '@src/utils/themeOrder';
import { themePreviewImage } from '@src/theme/previewImages';
import { useMatchesMedia, MOBILE_HEADER_MQ } from '@src/components/layout/useMobileHeaderRecede';
import { useStartPremiumPurchase } from '@src/components/PremiumUpsell/premiumInterest';
import { CrownIcon } from '@src/components/icons/CrownIcon';
import { PremiumUpsell } from '@src/components/PremiumUpsell/PremiumUpsell';
import { PreviewPill } from '@src/components/PreviewPill/PreviewPill';
import { DayNightToggle } from '@src/components/DayNightToggle/DayNightToggle';
import { SelectionCheck } from '@src/components/SelectionCheck/SelectionCheck';
import { PremiumFeatureBadge } from '@src/components/PremiumBadge/PremiumFeatureBadge';
import {
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { SEASONS, isSeason, DEFAULT_GAME_SEASON } from '@src/theme/season';
import { BoxTheme, PREMIUM_THEMES } from '@lub0v/santa-utils';
import { useTranslation } from 'react-i18next';
import { Button, Stack, Typography, Spinner, Hint } from '@src/components';
import { showDialog, closeDialog, useDialogOpen } from '@src/components/Dialog/Dialog';
import { ThemeContext } from '@src/theme/ThemeContext';
import { useTheme } from '@src/theme/useTheme';
import { loadSeason } from '@src/theme/registry';
import type { SeasonPackage } from '@src/theme/types';
import {
  trackThemePreview,
  trackThemeDayNightClick,
  trackThemeThumbnailClick,
  type ThemeAnalyticsContext,
} from '@src/utils/themeAnalytics';
import { BoxPicture } from '@src/components/BoxPicture/BoxPicture';
import { ChevronDownIcon } from '@src/components/icons/ChevronDownIcon';
import { EyeIcon } from '@src/components/icons/EyeIcon';
import './AppearancePicker.css';

const THEME_ORDER = SEASONS.map(season => season as BoxTheme);

export function GamePreview({
  pkg,
  name,
  picture,
  logo,
  mode,
  embedded = false,
  selectionContext,
}: {
  embedded?: boolean;
  selectionContext: ThemeAnalyticsContext;
  mode: 'light' | 'dark';
  pkg: SeasonPackage;
  name: string;
  picture: string;
  logo: string | null;
}) {
  const frame = useRef<HTMLIFrameElement>(null);
  const viewport = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);
  useEffect(() => {
    if (!embedded || !viewport.current) return;
    const node = viewport.current;
    let frame = 0;
    const update = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => setScale(node.clientWidth / 1200));
    };
    const observer = new ResizeObserver(update);
    observer.observe(node);
    update();
    return () => {
      observer.disconnect();
      window.cancelAnimationFrame(frame);
    };
  }, [embedded]);
  const { t, i18n } = useTranslation();
  const [renderedAssets, setRenderedAssets] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [timedOutRequest, setTimedOutRequest] = useState<string | null>(null);
  const requestId = JSON.stringify([pkg.id, name, picture, logo, i18n.language, mode, embedded, selectionContext, attempt]);
  // Text updates reuse the loaded scene. Only visual asset changes need a cover.
  const assetId = JSON.stringify([pkg.id, picture, logo, i18n.language, mode, embedded, selectionContext, attempt]);
  const ready = renderedAssets === assetId;
  const timedOut = timedOutRequest === requestId;
  const loadedRequests = useRef(new Set<string>());
  const [loadingRequest, setLoadingRequest] = useState<string | null>(null);
  useEffect(() => {
    if (ready) { loadedRequests.current.add(requestId); return; }
    // Cached artwork usually decodes within a frame or two. Avoid flashing a
    // progress indicator for those fast revisits, while retaining the asset gate.
    const timer = window.setTimeout(() => setLoadingRequest(requestId), loadedRequests.current.has(requestId) ? 1000 : 180);
    return () => window.clearTimeout(timer);
  }, [requestId, ready]);
  useEffect(() => {
    let acknowledged = false;
    const send = () =>
      frame.current?.contentWindow?.postMessage(
        {
          type: 'santa-theme-preview',
          draft: {
            requestId,
            theme: pkg.id,
            name,
            picture,
            logo,
            language: i18n.language,
            mode,
            sceneOnly: false,
            showModeToggle: !embedded,
            context: selectionContext,
          },
        },
        window.location.origin,
      );
    const receive = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || event.source !== frame.current?.contentWindow)
        return;
      if (event.data?.type === 'santa-theme-preview-ready') {
        setRenderedAssets(null);
        send();
      }
      if (event.data?.requestId === requestId && event.data?.type === 'santa-theme-preview-rendered') {
        acknowledged = true;
        window.clearInterval(retry);
        window.clearTimeout(timeout);
        setRenderedAssets(assetId);
        setLoadingRequest(null);
        setTimedOutRequest(null);
      }
      if (event.data?.type === 'santa-theme-preview-close') closeDialog();
    };
    window.addEventListener('message', receive);
    send();
    // Retry until the child confirms rendering, including cached loads and live reloads.
    const retry = window.setInterval(() => {
      if (!acknowledged) send();
    }, 500);
    const timeout = window.setTimeout(() => {
      window.clearInterval(retry);
      if (!acknowledged) setTimedOutRequest(requestId);
    }, 15000);
    return () => {
      window.removeEventListener('message', receive);
      window.clearInterval(retry);
      window.clearTimeout(timeout);
    };
  }, [pkg.id, name, picture, logo, i18n.language, attempt, mode, embedded, selectionContext, requestId, assetId]);
  return (
    <div
      ref={viewport}
      className={embedded ? 'appearance-desktop-preview' : 'appearance-preview-container'}
      data-preview-ready={ready}
      aria-busy={!ready}
    >
      {!ready && loadingRequest === requestId && (
        <div className="appearance-preview-loading" role="status">
          {timedOut ? (
            <Button
              onClick={() => {
                setTimedOutRequest(null);
                setAttempt((value) => value + 1);
              }}
            >
              {t('newBox.appearance.retry')}
            </Button>
          ) : (
            <div className="appearance-preview-progress">
              <span>{t('newBox.appearance.loading')}</span>
              <div className="appearance-preview-progress__track" aria-hidden="true"><span /></div>
            </div>
          )}
        </div>
      )}
      <iframe
        key={`${pkg.id}:${attempt}`}
        ref={frame}
        className="appearance-preview"
        data-ready={ready}
        title={t('newBox.appearance.preview')}
        src={`/theme-preview?season=${pkg.id}`}
        inert={embedded || undefined}
        tabIndex={embedded ? -1 : undefined}
        aria-hidden={embedded || undefined}
        scrolling={embedded ? 'no' : undefined}
        style={embedded ? { width: 1200, height: 760, transform: `scale(${scale})` } : undefined}
      />
      {embedded && <div className="appearance-preview-gesture-surface" aria-hidden="true" />}
    </div>
  );
}

/** Shared theme scene and controls for selection cards and reminder dialogs. */
export function ThemePreviewScene({ pkg, name, picture, logo, mode, onModeChange, onPreview,
  selectionContext, hideScene = false, showPreview = true, disabled = false,
}: {
  pkg?: SeasonPackage | null; name: string; picture: string; logo: string | null;
  mode: 'light' | 'dark'; onModeChange: (mode: 'light' | 'dark') => void;
  onPreview: () => void; selectionContext: ThemeAnalyticsContext;
  hideScene?: boolean; showPreview?: boolean; disabled?: boolean;
}) {
  const { t } = useTranslation();
  const paletteId = useId();
  const paletteScope = `[data-preview-palette="${paletteId}"]`;
  const colors = pkg?.colorsCss.replace(/:root/g, paletteScope)
    .replace(/\[data-theme='(light|dark)'\]/g, `${paletteScope}[data-preview-mode='$1']`);
  return <div className="appearance-featured__scene" data-preview-palette={paletteId} data-preview-mode={mode}>
    {colors && <style>{colors}</style>}
    {hideScene ? <div className="appearance-desktop-preview" /> : pkg ?
      <GamePreview selectionContext={selectionContext} embedded pkg={pkg} mode={mode}
        name={name} picture={picture} logo={logo} /> : <Spinner size="medium" />}
    <DayNightToggle className="appearance-mode" compact value={mode} onChange={next => {
      if (pkg) trackThemeDayNightClick(pkg.id as BoxTheme, selectionContext);
      onModeChange(next);
    }} />
    {showPreview && <PreviewPill className="appearance-preview-control appearance-featured__open"
      icon={<EyeIcon />} disabled={!pkg || disabled} onClick={onPreview}>
      {t('newBox.appearance.preview')}
    </PreviewPill>}
  </div>;
}

export function AppearancePicker({
  theme: savedTheme,
  name,
  picture,
  logo,
  disabled,
  onThemeChange,
  initialBrowsedTheme,
  onBrowseTheme,
  showPreview = true,
  inDialog = false,
  selectionContext = 'creation',
  onThumbnailClick,
  themeOrder,
  isPremium = false,
  showPictures = true,
  children,
}: {
  theme: BoxTheme;
  name: string;
  picture: string;
  logo: string | null;
  disabled: boolean;
  onThemeChange: (theme: BoxTheme) => void;
  initialBrowsedTheme?: BoxTheme;
  onBrowseTheme?: (theme: BoxTheme) => void;
  showPreview?: boolean;
  /** The picker itself is the dialog content; keep its embedded scene visible. */
  inDialog?: boolean;
  selectionContext?: ThemeAnalyticsContext;
  onThumbnailClick?: (theme: BoxTheme, placement: ThemeClickPlacement) => void;
  themeOrder?: ThemeOrderAssignment;
  isPremium?: boolean;
  showPictures?: boolean;
  children: ReactNode;
}) {
  const theme = isSeason(savedTheme) ? savedTheme as BoxTheme : DEFAULT_GAME_SEASON;
  const startPremiumPurchase = useStartPremiumPurchase();
  const offer = usePremiumOffer('themes');
  const context = useTheme();
  const mobile = useMatchesMedia(MOBILE_HEADER_MQ);
  const dialogOpen = useDialogOpen();
  const [pictureExpanded, setPictureExpanded] = useState(false);
  const pictureOptionsId = useId();
  const pictureTrigger = useRef<HTMLButtonElement>(null);
  const featuredRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const featured = featuredRef.current;
    if (!featured) return;
    const scene = featured.querySelector<HTMLElement>('.appearance-featured__scene');
    const caption = featured.querySelector<HTMLElement>('.appearance-featured__caption');
    const heading = featured.querySelector<HTMLElement>('.appearance-featured__title-row');
    const selector = featured.querySelector<HTMLElement>('.appearance-picture-selector');
    if (!scene || !caption || !heading || !selector) return;
    const resize = () => {
      const grid = selector.querySelector<HTMLElement>('.picture-picker');
      if (!grid) return;
      const rows = Math.ceil(grid.children.length / 4);
      if (!rows) return;
      const gap = parseFloat(getComputedStyle(grid).rowGap) || 0;
      const captionGap = parseFloat(getComputedStyle(caption).rowGap) || 0;
      const iconHeading = selector.querySelector<HTMLElement>(
        '.appearance-picture-selector__title',
      );
      const iconHeadingHeight = iconHeading?.offsetHeight ?? 0;
      const selectorGap = iconHeadingHeight
        ? parseFloat(getComputedStyle(selector).rowGap) || 0
        : 0;
      const availableHeight =
        scene.clientHeight - heading.offsetHeight - captionGap - iconHeadingHeight - selectorGap;
      const size = Math.max(
        32,
        Math.min((availableHeight - gap * (rows - 1)) / rows, (selector.clientWidth - gap * 3) / 4),
      );
      featured.style.setProperty('--appearance-icon-size', `${Math.floor(size)}px`);
    };
    const observer = new ResizeObserver(resize);
    [scene, heading, selector].forEach((element) => observer.observe(element));
    const contentObserver = new MutationObserver(resize);
    contentObserver.observe(selector, { childList: true, subtree: true });
    resize();
    return () => {
      observer.disconnect();
      contentObserver.disconnect();
    };
  }, []);
  const closePictureOptions = () => {
    setPictureExpanded(false);
    if (pictureTrigger.current?.getClientRects().length) pictureTrigger.current.focus();
  };
  const { t, i18n } = useTranslation();
  // context.theme is the resolved day/night mode, including Auto's OS choice.
  const [previewMode, setPreviewMode] = useState<'light' | 'dark'>(() =>
    selectionContext === 'creation' ? context.theme : 'dark',
  );
  const [browsedTheme, setBrowsedTheme] = useState(initialBrowsedTheme && isSeason(initialBrowsedTheme) ? initialBrowsedTheme : theme);
  const [previousTheme, setPreviousTheme] = useState(theme);
  if (previousTheme !== theme) {
    setPreviousTheme(theme);
    setBrowsedTheme(theme);
  }
  const [packages, setPackages] = useState<Partial<Record<BoxTheme, SeasonPackage>>>({});
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    setFailed(false);
    // Thumbnails are static images. Only load packages needed for the current
    // preview and selected icon controls, not all seven themes on entry.
    for (const option of new Set([theme, browsedTheme])) {
      void loadSeason(option)
        .then((pkg) => {
          if (active) setPackages((loaded) => ({ ...loaded, [pkg.id]: pkg }));
        })
        .catch(() => {
          if (active) setFailed(true);
        });
    }
    return () => {
      active = false;
    };
  }, [theme, browsedTheme]);
  const pkg = packages[theme];
  const previewPkg = packages[browsedTheme];
  const premiumPreview = PREMIUM_THEMES.includes(browsedTheme) && !isPremium;
  const openPreview = () => {
    if (!previewPkg) return;
    trackThemePreview(browsedTheme, selectionContext);
    showDialog({
      title: t('newBox.appearance.preview'),
      children: (
        <GamePreview
          selectionContext={selectionContext}
          pkg={previewPkg}
          name={name}
          picture={picture}
          logo={logo}
          mode={previewMode}
        />
      ),
      fullScreen: true,
      immersive: true,
      closeLabel: t('newBox.appearance.close'),
      testId: 'theme-full-preview',
      onCancel: closeDialog,
    });
  };
  const premiumOffer = premiumPreview ? (
    <PremiumUpsell feature="themes" context={selectionContext} compact={!mobile && selectionContext !== 'settings'} />
  ) : null;
  return (
    <Stack gap={5} wide className={`appearance-picker appearance-picker--${selectionContext}`}>
      <section ref={featuredRef} className={`appearance-featured${selectionContext !== 'profile' && premiumPreview ? ' appearance-featured--premium' : ''}`}>
        <ThemePreviewScene pkg={previewPkg} name={name}
          picture={premiumPreview ? 'santa' : picture} logo={premiumPreview ? null : logo}
          mode={previewMode} onModeChange={setPreviewMode} onPreview={openPreview}
          selectionContext={selectionContext} hideScene={dialogOpen && !inDialog}
          showPreview={showPreview} disabled={disabled} />
        <div className="appearance-featured__caption">
          <div className="appearance-featured__title-row">
            <div className="appearance-featured__heading">
              <Typography as="h3" role="title/section">
                {t(`newBox.appearance.${browsedTheme}`)}
              </Typography>
              <div className="appearance-featured__badge">
                {PREMIUM_THEMES.includes(browsedTheme) ? (
                  <>
                    <PremiumFeatureBadge feature="themes">{t('newBox.appearance.premium')}</PremiumFeatureBadge>
                  </>
                ) : (
                  <span className="appearance-option__badge">{t('newBox.appearance.free')}</span>
                )}
              </div>
            </div>
            {showPictures && !premiumPreview && (
              <button
                ref={pictureTrigger}
                type="button"
                className="appearance-picture-trigger"
                aria-label={t('newBox.appearance.picture')}
                aria-expanded={pictureExpanded}
                aria-controls={pictureOptionsId}
                disabled={disabled || !pkg}
                onClick={() => setPictureExpanded((open) => !open)}
              >
                <span className="appearance-picture-trigger__label">
                  {t('newBox.appearance.icon')}
                </span>
                {pkg && (
                  <ThemeContext.Provider value={{ ...context, season: pkg.id, seasonPackage: pkg }}>
                    <BoxPicture picture={picture} logo={logo} />
                  </ThemeContext.Provider>
                )}
                <ChevronDownIcon />
              </button>
            )}
            <p className="appearance-featured__description">
              {t(`newBox.appearance.descriptions.${browsedTheme}`)}
            </p>
          </div>
          {selectionContext === 'profile' && premiumPreview && <Button variant="premium-dark" size="small" icon={<CrownIcon />} className="appearance-profile-upgrade" onClick={() => startPremiumPurchase('theme_selector_upgrade', 'themes', selectionContext)} testId="account-theme-upgrade"><PremiumOfferView placement="theme_selector_upgrade" feature="themes" context="profile">{isTieredVariant(offer.variant) ? premiumFeatureAction('themes', i18n.language, offer.symbolPrice) : t('premiumModal.subscribe')}</PremiumOfferView></Button>}
          {!mobile && selectionContext !== 'profile' && premiumOffer}
          <div
            hidden={premiumPreview || !showPictures}
            id={pictureOptionsId}
            className="appearance-picture-selector"
            data-expanded={pictureExpanded}
            onClick={(event) => {
              const option = (event.target as Element).closest<HTMLButtonElement>(
                'button[role="option"]',
              );
              if (option && !option.disabled) closePictureOptions();
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.stopPropagation();
                closePictureOptions();
              }
            }}
          >
            <h4 className="appearance-picture-selector__title">{t('newBox.appearance.icon')}</h4>
            {pkg ? (
              <ThemeContext.Provider value={{ ...context, season: pkg.id, seasonPackage: pkg }}>
                {children}
              </ThemeContext.Provider>
            ) : (
              !failed && <Spinner size="medium" />
            )}
          </div>
        </div>
      </section>
      <div className="appearance-themes">
        <div className="appearance-options" role="group" aria-label={t('newBox.appearance.theme')}>
          {(themeOrder?.themes ?? THEME_ORDER).map((option, index) => {
            const premiumTheme = PREMIUM_THEMES.includes(option);
            return (
              <div key={option} data-theme={option} data-premium={premiumTheme} data-selected={browsedTheme === option} className="appearance-option">
                <button
                  type="button"
                  className="appearance-option__select"
                  disabled={disabled}
                  aria-label={t(`newBox.appearance.${option}`)}
                  aria-pressed={browsedTheme === option}
                  onClick={() => {
                    const placement: ThemeClickPlacement = {
                      theme_position: index + 1,
                      ...(themeOrder ? {
                        theme_order_variant: themeOrder.variant,
                        theme_order_experiment: THEME_ORDER_EXPERIMENT,
                      } : {}),
                    };
                    trackThemeThumbnailClick(option, selectionContext, placement);
                    onThumbnailClick?.(option, placement);
                    setBrowsedTheme(option);
                    onBrowseTheme?.(option);
                    setPictureExpanded(false);
                    if (!premiumTheme || isPremium) onThemeChange(option);
                  }}
                />
                <div
                  aria-hidden="true"
                  className="appearance-miniature-slot"
                >
                  <img
                    src={themePreviewImage(option, previewMode)}
                    alt=""
                    draggable={false}
                    decoding="async"
                    width={1200}
                    height={800}
                  />

                </div>
                <div className="appearance-option__label">
                  <div className="appearance-option__title">
                    <strong>{t(`newBox.appearance.${option}`)}</strong>
                    <div className="appearance-option__status">
                      {browsedTheme === option && <SelectionCheck />}
                    </div>
                  </div>
                  <span className="appearance-option__description">
                    {t(`newBox.appearance.descriptions.${option}`)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {mobile && selectionContext !== 'profile' && premiumOffer}
      {failed && <Hint tone="error">{t('newBox.appearance.failed')}</Hint>}
    </Stack>
  );
}
